import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TradingPartnerService } from '../../../services/trading-partner.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { permissions } from '../../../permissions';
import { ToastrService } from 'ngx-toastr';
import { Subscription} from 'rxjs/Subscription';
import { AuthService } from '../../../auth/auth.service';
import { MatDialog } from '@angular/material';
import { UploadTradingPartnerComponent } from '../../../components';
import { ConfirmationService } from 'primeng/api';
import { saveAs } from '@progress/kendo-file-saver';
import { TradingPartnerSaveModalComponent } from '../trading-partner-save-modal/trading-partner-save-modal.component';
import { environment} from '../../../../environments/environment';

@Component({
  selector: 'app-tp-new-customer',
  templateUrl: './tp-new-customer.component.html',
  styleUrls: ['./tp-new-customer.component.scss'],
  providers: [ConfirmationService],
})
export class TpNewCustomerComponent implements OnInit {
  public model: any = {};
  customerFormGroup: FormGroup;
  displayMessage: { [key: string]: string } = {};
  frameTypes: string[] = [];
  public errors: any[];
  pageTitle = 'New Customer';
  errorMessage: string;
  newFields: any[];
  thereAreNewFields = false;
  loading = false;
  subscriptions: Subscription[] = [];
  customer: any;
  ticket: any;
  hasEditPermission = false;
  isSuperAdmin = false;
  canCreateTradingPartner = false;
  public permissions = permissions;
  newRecord = false;
  status = '0';
  statusEmail = '0';
  companyId = '0';
  hasFixedCompany = false;
  companies: any = [];
  selectedCompany = '0';
  submitEnabled = false;
  formAction = '';
  formActions;
  cu1CreatedById: string;
  hideCancelButton = false;
  ticketUrl = '';
  customerFileList: any = [];
  customerFiles: File[];
  maxNumberOfFiles = 20;
  filesUpdated = false;
  maxSize: number;
  maxQuantity: number;
  isGuestUser = false;
  contactEmails: any = [];

  constructor(
    private formBuilder: FormBuilder,
    public title: Title,
    private route: ActivatedRoute,
    private router: Router,
    private tradingPartnerService: TradingPartnerService,
    private toastr: ToastrService,
    private permissionsService: NgxPermissionsService,
    private authService: AuthService,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
  ) {
    this.title.setTitle('EDI - TP Customer');
  }

  ngOnInit() {
    this.formActions = this.tradingPartnerService.getFormActions();
    this.frameTypes = this.tradingPartnerService.getFrameTypes();
    this.customerFormGroup = this.setFormGroup();

    this.permissionsService
      .hasPermission([
        this.permissions.EDI_MAINT_EDIT,
        this.permissions.EDI_MAINT_EDIT_TOKEN,
      ])
      .then((hasPermit) => {
        this.hasEditPermission = hasPermit;
      });

    this.permissionsService
        .hasPermission([
          this.permissions.ADMIN,
        ])
        .then((hasPermit) => {
          this.isSuperAdmin = hasPermit;
        });

    this.permissionsService
        .hasPermission([this.permissions.EDI_TRADING_PARTNER_ADD])
        .then((hasPermit) => {this.canCreateTradingPartner = hasPermit;
        });

    this.newFields = [];
    this.loading = true;

    if ( this.authService.userId === environment.guestUserId ) {
      this.isGuestUser = true;
    }

    const params = this.route.snapshot.params;

    if (params['id'] !== '0' || params['hash']) {
      const id = params['id'] || params['hash'];
      this.companyId = params['CO1_ID'] || '0';
      if (params['hash'] && this.mustHideCancelButton()) {
        this.hideCancelButton = true;
      }

      this.subscriptions.push(
        this.tradingPartnerService.getCustomer(id, this.companyId).subscribe(
          (result) => {
            if (result && result.data) {
              this.onCustomerRetrieved(result.data);
              this.getNewFields();
              this.checkEmail(result.data.ticket[0].CU2_CONTACT_EMAIL);
              this.companyId = result.data.customer.CO1_ID || this.companyId;
              this.validateCompany(this.companyId);
            } else {
              this.errorMessage = 'There was an error loading the customer';
              this.loading = false;
            }
          },
          (error: any) => {
            this.errorMessage = <any>error;
            this.loading = false;
          }
        )
      );
    } else {
      this.pageTitle = 'Add Customer';
      this.status = '0';
      this.newRecord = true;
      this.getNewFields();
      this.companyId = params['CO1_ID'];
      this.validateCompany(this.companyId);
    }
    this.customerFileList = [];
    this.customerFiles = [];
    this.maxQuantity = 20;
    this.maxSize = 10 * 1024 * 1024;
  }

  ngOnDestroy = () => {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
    }
  };

  onCancel() {
    if (this.router.url.startsWith('/customers')) {
      this.router.navigate(['customers']);
    } else {
      this.router.navigate(['trading-partners']);
    }
  }

  onCustomerRetrieved(data: any): void {
    if (this.customerFormGroup) {
      this.customerFormGroup.reset();
    }
    this.customer = data.customer[0] || data.customer;
    this.ticket = data.ticket[0] || data.ticket;

    this.pageTitle = `Customer: ${this.customer.CU1_NAME}`;
    this.status = this.customer.CU1_STATUS.toString();

    if (this.customer.CU1_CREATED_BY !== 0) {
      const split = this.customer.CU1_CREATED_BY.split('|');
      this.customer.CU1_CREATED_BY = split[0];
      this.cu1CreatedById = split[1];
    }

    this.populateCustomer();
    this.populateTicket();
    this.populateFileList(data);
  }

  onCompanySelectionChanged(value) {
    this.selectedCompany = value;
  }

  setFormGroup() {
    return this.formBuilder.group({
      CO1_ID: ['', [Validators.required]],
      CU1_STATUS: [''],
      CU1_ID: [{ value: '', disabled: true }],
      CORP_ADDRESS_ID: [
        '',
        [
          this.conditionalValidator(
            () =>
              ['0'].includes(this.status) &&
              [this.formActions.SUBMITTING].includes(this.formAction),
            Validators.required
          ),
        ],
      ],
      CU1_NAME: ['', [Validators.required]],
      CU1_CREATED_BY: [{ value: '', disabled: true }],
      CU1_CREATED_ON: [{ value: '', disabled: true }],
      CU1_MODIFIED_BY: [{ value: '', disabled: true }],
      CU1_MODIFIED_ON: [{ value: '', disabled: true }],
      CU1_SHOW_DEFAULT: [{ value: '', disabled: true }],
      CU1_TEST_MODE: [false],
      CU1_COMPANY_ID: [''],
      CU1_USE_P21_API: [''],
      CU1_RECEIVE_EDI: [''],
      CU1_SEND_EDI_INVOICES: [''],
      CU1_SEND_EDI_ASN: [''],
      CU1_SEND_EDI_ORDERS: [''],
      CU1_SEND_EDI_ORDER_CONFIRMATIONS: [''],
      CU1_SEND_ACKNOWLEDGEMENT: [''],
      CU1_SEND_PACKING_SLIPS: [''],
      CU1_ORDER_TYPE: [''],
      CU1_ORDER_FORMAT: [''],
      CU1_INVOICE_FORMAT: [''],
      CU1_ASN_FORMAT: [''],
      CU1_TXT_APPROVED: [''],
      CU1_SEND_FTP: [''],
      CU1_SEND_SFTP: [''],
      CU1_POST_HTTP: [''],
      CU1_RECEIVE_FTP: [''],
      CU1_PICKUP_FTP: [''],
      CU1_RECEIVE_HTTP: [''],
      CU1_PICKUP_SFTP: [''],
      CU1_REMOTE_FTP_SERVER: [''],
      CU1_REMOTE_FTP_USERNAME: [''],
      CU1_REMOTE_FTP_PASSWORD: [''],
      CU1_REMOTE_FTP_DIRECTORY_SEND: [''],
      CU1_REMOTE_FTP_DIRECTORY_PICKUP: [''],
      CU1_REMOTE_FTP_DELETE_FILES_AFTER_PICKUP: [''],
      CU1_FTP_USER: [''],
      CU1_FTP_PASSWORD: [''],
      CU1_FTP_DIRECTORY: [''],
      CU1_REMOTE_HTTP_SERVER: [''],
      CU1_REMOTE_HTTP_USERNAME: [''],
      CU1_REMOTE_HTTP_PASSWORD: [''],
      CU1_SUPPLIER_CODE: [''],
      CU1_RECEIVER_QUALIFIER: [''],
      CU1_RECEIVER_ID: [''],
      CU1_RECEIVER_GS_ID: [''],
      CU1_FACILITY: [''],
      CU1_TRADING_PARTNER_QUALIFIER: [''],
      CU1_TRADING_PARTNER_ID: [''],
      CU1_TRADING_PARTNER_GS_ID: [''],
      CU1_ASN_TRADING_PARTNER_ID: [''],
      CU1_CONSOLIDATE_ASN: [''],
      CU1_FLAG: [''],
      CU1_X12_STANDARD: [''],
      CU1_EDI_VERSION: [''],
      CU1_DUNS: [''],
      CU1_SHARED_SECRET: [''],
      CU1_REJECT_INVALID_ITEM_ORDERS: [''],
      CU1_INVALID_ITEM_SUBSTITUTE: [''],
      CU1_USE_CONTRACT: [''],
      CU1_SEND_CUSTOMERS_AND_ITEMS: [''],
      CU1_STOP_IMPORT_WITH_ERRORS: [''],
      CU1_USE_CLASS_ID: [''],
      CU1_USE_CUSTOMER_ID: [''],
      CU1_CLASS_ID: [''],
      CU1_MAP: [''],
      CU1_ORDER_PRICE_OVERRIDE: [''],
      CU1_SEND_CREDIT_INVOICES: [''],
      CU1_ONLY_SEND_CREDIT_INVOICES: [''],
      CU1_SEND_PAID_INVOICES: [''],
      CU1_852_IMPORT_FOLDER: [''],
      CU1_ALWAYS_SEND_ORDER_CONFIRMATIONS: [''],
      CU1_COMPLETE_SHIP_TO_NAME: [''],
      CU1_ALWAYS_SEND_ASNS: [''],
      CU1_IMPORT_FREIGHT_CODES: [''],
      CU1_POST_AS2: [''],
      CU1_RECEIVE_AS2: [''],
      CU1_CXML_PAYLOAD_ID: [''],
      CU1_AS2_CERTIFICATE_FILENAME: [''],
      CU1_AS2_RECEIVER_ID: [''],
      CU1_AS2_TRADING_PARTNER_ID: [''],
      CU1_USE_P21_SHIP_TO_DATA: [''],
      CU1_CUSTOMER_SENDS_P21_SHIP_TO_ID: [''],
      CU1_ALLOW_DUPLICATE_PO_NUMBERS: [''],
      CU1_ASN_IMPORT_FOLDER: [''],
      CU1_PICKUP_DIRECTORY: [''],
      CU1_PICKUP_HEADER_PREFIX: [''],
      CU1_PICKUP_LINE_PREFIX: [''],
      CU1_SEND_INVENTORY: [''],
      CU1_INVENTORY_FORMAT: [''],
      CU1_AS2_REQUEST_RECEIPT: [''],
      CU1_AS2_ENCRYPT_MESSAGES: [''],
      CU1_AS2_SIGN_MESSAGES: [''],
      CU1_AS2_KEY_LENGTH: [''],
      CU1_AS2_ENCRYPTION_ALGORITHM: [''],
      CU1_AS2_SIGNING_ALGORITHM: [''],
      CU1_AS2_COMPATIBILITY_MODE: [''],
      CU1_SEND_PRICE_CATALOG: [''],
      CU1_PRICE_CATALOG_FORMAT: [''],
      CU1_ORDER_FORWARDING_FOLDER: [''],
      CU1_PRICE_CATALOG_IMPORT_FOLDER: [''],
      CU1_INVOICE_IMPORT_FOLDER: [''],
      CU1_PICKUP_EBAY: [''],
      CU1_EBAY_TOKEN: [''],
      CU1_EBAY_DEV_NAME: [''],
      CU1_EBAY_APP_NAME: [''],
      CU1_EBAY_CERT_NAME: [''],
      CU1_SEND_EDI_PICK_TICKETS: [''],
      CU1_PICK_TICKET_FORMAT: [''],
      CU1_PICK_TICKET_LOCATION_ID: [''],
      CU1_SAVE_867_IN_DATABASE: [''],
      CU1_SEND_ORDER_CHECK: [''],
      CU1_ORDER_CHECK_EMAIL: [''],
      CU1_SHIP_TO_FILTER: [''],
      CU1_REMOTE_FTP_DIRECTORY_SEND_INVENTORY: [''],
      CU1_PICKUP_AMAZON: [''],
      CU1_AMAZON_MWS_MARKET_ID_US: [''],
      CU1_AWS_ACCESS_KEY: [''],
      CU1_AWS_SECRET_KEY: [''],
      CU1_AWS_SELLER_ID: [''],
      CU1_AWS_MARKETPLACE_ID: [''],
      CU1_AWS_UTC_HOUR_OFFSET: [''],
      CU1_PICKUP_BILLTRUST: [''],
      CU1_BILLTRUST_DEVELOPER_GUID: [''],
      CU1_BILLTRUST_CLIENT_GUID: [''],
      CU1_PICKUP_VERSAPAY: [''],
      CU1_VERSAPAY_TOKEN: [''],
      CU1_VERSAPAY_KEY: [''],
      CU1_SEND_EDI_QUOTES: [''],
      CU1_QUOTE_FORMAT: [''],
      CU1_FTP_DOWNLOAD_FILTER: [''],
      CU1_FTP_DOWNLOAD_FILTER2: [''],
      CU1_FTP_DOWNLOAD_EXCLUDE: [''],
      CU1_CHECK_ACKNOWLEDGEMENTS: [''],
      CU1_CHECK_ASN_ACKNOWLEDGEMENTS: [''],
      CU1_830_FORWARDING_FOLDER: [''],
      CU1_PICKUP_CHANNEL_ADVISOR: [''],
      CU1_CHANNEL_ADVISOR_DEV_KEY: [''],
      CU1_CHANNEL_ADVISOR_PASSWORD: [''],
      CU1_CHANNEL_ADVISOR_ACCOUNT_ID: [''],
      CU1_CHANNEL_ADVISOR_LOCAL_ID: [''],
      CU1_SFTP_PRIVATE_KEY_FILENAME: [''],
      CU1_ALWAYS_SEND_INVOICES: [''],
      CU1_QUOTE_IMPORT_FOLDER: [''],
      CU1_SEND_SINGLE_LINE_INVOICES: [''],
      CU1_INVOICE_FORWARDING_FOLDER: [''],
      CU1_PICKUP_EMAIL: [''],
      CU1_EMAIL_SERVER: [''],
      CU1_EMAIL_USER: [''],
      CU1_EMAIL_PASSWORD: [''],
      CU1_EMAIL_PORT: [''],
      CU1_EMAIL_REQUIRES_TLS: [''],
      CU1_PICKUP_SELLER_CLOUD: [''],
      CU1_SELLER_CLOUD_USERNAME: [''],
      CU1_SELLER_CLOUD_PASSWORD: [''],
      CU1_SELLER_CLOUD_PICKUP_FBA: [''],
      CU1_ASN_FORWARDING_FOLDER: [''],
      CU1_SEND_CONTRACT_ORDERS: [''],
      CU1_SEND_EDI_TRANSFERS: [''],
      CU1_TRANSFER_FORMAT: [''],
      CU1_TRANSFER_LOCATION_ID: [''],
      CU1_SEND_INVENTORY_RECEIPT_ORDERS: [''],
      CU1_SEND_EDI_ROUTING_REQUESTS: [''],
      CU1_ROUTING_REQUEST_FORMAT: [''],
      CU1_PICKUP_MAGENTO: [''],
      CU1_MAGENTO_VERSION: [''],
      CU1_MAGENTO_USERNAME: [''],
      CU1_MAGENTO_PASSWORD: [''],
      CU1_MAGENTO_API_KEY: [''],
      CU1_ORDER_CONFIRMATION_IMPORT_FOLDER: [''],
      CU1_RECEIVE_API_CALLS: [''],
      CU1_API_USERNAME: [''],
      CU1_API_PASSWORD: [''],
      CU1_PICKUP_CTL_SHIPMENTS: [''],
      CU1_CTL_CLIENT_ID: [''],
      CU1_CTL_USER_ID: [''],
      CU1_IMPORT_SCHEDULED_RELEASES_INTO_P21: [''],
      CU1_PICKUP_CTL_RMA_INFO: [''],
      CU1_SYNCHRONIZED: [''],
      CU1_PICKUP_BIG_COMMERCE: [''],
      CU1_BIG_COMMERCE_ACCESS_TOKEN: [''],
      CU1_BIG_COMMERCE_CLIENT_ID: [''],
      CU1_BIG_COMMERCE_CLIENT_SECRET: [''],
      CU1_REMOTE_ORDER_DATABASE: [''],
      CU1_REMOTE_ORDER_TABLE: [''],
      CU1_CUSTOMER_LIST_URL: [''],
      CU1_CONSOLIDATE_INVOICES: [''],
      CU1_SEND_EMAIL: [''],
      CU1_EMAIL: [''],
      CU1_INVOICE_PDF_IMPORT_FOLDER: [''],
      CU1_SEND_EDI_RMAS: [''],
      CU1_SPECIAL_ORDER_NUMBERS: [''],
      CU1_SPECIAL_ASN_NUMBERS: [''],
      CU1_SPECIAL_INVOICE_NUMBERS: [''],
      CU1_CUT_OFF_DATE: [''],
      CU1_INVENTORY_IMPORT_FOLDER: [''],
      CU1_SEGMENT_TERMINATOR: [''],
      CU1_PICKUP_ZENTAIL: [''],
      CU1_ZENTAIL_TOKEN: [''],
      CU1_ORDER_EMAIL: [''],
      CU1_ORDER_CHANGE_EMAIL: [''],
      CU1_ORDER_RELEASE_EMAIL: [''],
      CU1_ACKNOWLEDGEMENT_EMAIL: [''],
      CU1_PRODUCT_TRANSFER_EMAIL: [''],
      CU1_WAREHOUSE_ORDER_EMAIL: [''],
      CU1_RETURNS_EMAIL: [''],
      CU1_SHIP_CONFIRMATION_EMAIL: [''],
      CU1_ASN_EMAIL: [''],
      CU1_ORDER_CONFIRMATION_HISTORY_NUMBER_RANGE: [''],
      CU1_ORDER_CONFIRMATION_HISTORY_TIMEFRAME: [''],
      CU1_ORDER_CONFIRMATION_HISTORY_TIMEFRAME_TYPE: [''],
      CU1_ASN_HISTORY_NUMBER_RANGE: [''],
      CU1_ASN_HISTORY_TIMEFRAME: [''],
      CU1_ASN_HISTORY_TIMEFRAME_TYPE: [''],
      CU1_INVOICE_HISTORY_NUMBER_RANGE: [''],
      CU1_INVOICE_HISTORY_TIMEFRAME: [''],
      CU1_INVOICE_HISTORY_TIMEFRAME_TYPE: [''],
      CU1_TAKER: [''],
      CU1_ELEMENT_SEPARATOR: [''],
      CU1_SEND_AUTO_POS: [''],
      CU1_AUTO_PO_LOCATIONS: [''],
      CU1_SOLVOYO_USERNAME: [''],
      CU1_SOLVOYO_PASSWORD: [''],
      CU1_SEND_EDI_LOCATIONS: [''],
      CU1_LOCATION_FORMAT: [''],
      CU1_SOURCE_IP_ADDRESS: [''],
      CU1_SEND_EDI_ORDER_POS: [''],
      CU1_ORDER_PO_FORMAT: [''],
      CU1_SEND_EDI_BOM: [''],
      CU1_BOM_FORMAT: [''],
      CU1_PICKUP_WOOCOMMERCE: [''],
      CU1_WOOCOMMERCE_CONSUMER_KEY: [''],
      CU1_WOOCOMMERCE_CONSUMER_SECRET: [''],
      CU1_WOOCOMMERCE_INVENTORY_URL: [''],
      CU1_PICKUP_SHOPIFY: [''],
      CU1_SHOPIFY_API_KEY: [''],
      CU1_SHOPIFY_PASSWORD: [''],
      CU1_COMPARE_ITEMS: [''],
      CU1_CONTACT_ID: [''],
      CU1_REMITTANCE_ADVICE_FORWARDING_FOLDER: [''],
      CU1_REJECT_ORDER_CHANGES: [''],
      CU1_ENABLE_ORDER_ENTRY: [''],
      CU1_ENABLE_ORDER_ENTRY_MASTER_DATA_SYNCHRONIZATION: [''],
      CU1_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION: [''],
      CU1_MASTER_CUSTOMER_CODE: [''],
      CU1_MASTER_CUSTOMER_NAME: [''],
      CU1_ORDER_ENTRY_USE_BINS: [''],
      CU1_ORDER_ENTRY_TYPE: [''],
      CU1_ALLOW_DUPLICATE_BARCODES: [''],
      CU1_ALLOW_INVENTORY_MANAGEMENT: [''],
      CU1_INVENTORY_LOCATION: [''],
      CU1_PICKUP_3DCART: [''],
      CU1_3DCART_SECURE_URL: [''],
      CU1_3DCART_PRIVATE_KEY: [''],
      CU1_3DCART_TOKEN: [''],
      CU1_PICKUP_WORKWAVE: [''],
      CU1_WORKWAVE_KEY: [''],
      CU1_PRICE_CATALOG_SUPPLIER_ID: [''],
      CU1_PRICE_CATALOG_ONLY_STOCKABLE: [''],
      CU1_PRICE_CATALOG_PRODUCT_GROUP_ID: [''],
      CU1_VERACORE_USERNAME: [''],
      CU1_VERACORE_PASSWORD: [''],
      CU1_PICKUP_VERACORE_SHIPMENTS: [''],
      CU1_VERACORE_SYSTEM_ID: [''],
      CU1_ENABLE_850_FORWARDING: [''],
      CU1_TRADING_PARTNER_FORWARDING_QUALIFIER: [''],
      CU1_TRADING_PARTNER_FORWARDING_ID: [''],
      CU1_TRADING_PARTNER_FORWARDING_GS_ID: [''],
      CU1_AS2_RECEIVE_ID: [''],
      CU1_PRICE_CATALOG_ONLY_DIFFERENTIAL: [''],

      CU1_ORDER_IMPORT_FOLDER: [''],
      CU1_ORDER_FILE_EXTENSION: [''],
      CU1_CLOUDSUITE_CONFIGURATION: [''],

      CU1_SEND_CONTACTS: [''],
      CU1_SEND_CUSTOMERS: [''],
      CU1_CONTACT_FORMAT: [''],
      CU1_CUSTOMER_FORMAT: [''],

      CU1_PICKUP_AERO_SHIPMENTS: [''],
      CU1_AERO_USERNAME: [''],
      CU1_AERO_PASSWORD: [''],
      CU1_PICKUP_FREIGHTWISE_SHIPMENTS: [''],
      CU1_FREIGHTWISE_USERNAME: [''],
      CU1_FREIGHTWISE_PASSWORD: [''],
      CU1_HUBSPOT_API_KEY: [''],

      CU1_VALIDATE: [''],
      CU1_VALIDATION_ORDER_TEMPLATE: [''],
      CU1_VALIDATION_ORDER_CONFIRMATION_TEMPLATE: [''],
      CU1_VALIDATION_ASN_TEMPLATE: [''],
      CU1_VALIDATION_INVOICE_TEMPLATE: [''],
      CU1_VALIDATION_REPORT_EMAIL: [''],

      TICKET: this.formBuilder.group({
        CU2_ID: [0],
        CU2_OUTBOUND_855_ORDER_CONFIRMATION: [false],
        CU2_OUTBOUND_856_ADVANCED_SHIP_NOTICE: [false],
        CU2_OUTBOUND_810_INVOICE: [false],
        CU2_OUTBOUND_846_INVENTORY_INQUIRY: [false],
        CU2_OUTBOUND_832_PRICE_CATALOG: [false],
        CU2_OUTBOUND_753_ROUTE_REQUEST: [false],
        CU2_OUTBOUND_OTHER: [''],
        CU2_INBOUND_850_PURCHASE_ORDER: [false],
        CU2_INBOUND_830_RELEASE_SCHEDULE: [false],
        CU2_INBOUND_860_ORDER_CHANGE: [false],
        CU2_INBOUND_820_PAYMENT_ADVICE: [false],
        CU2_INBOUND_754_ROUTING: [false],
        CU2_INBOUND_OTHER: [''],
        TICKET_NUMBER: [''],
        CU2_CREATED_BY: [{ value: '', disabled: true }],
        CU2_CREATED_ON: [{ value: '', disabled: true }],
        CU2_MODIFIED_BY: [{ value: '', disabled: true }],
        CU2_MODIFIED_ON: [{ value: '', disabled: true }],

        CU2_CONTACT_NAME: [''],
        CU2_CONTACT_PHONE: [''],
        CU2_CONTACT_EMAIL: [''],
      }),
    });
  }

  populateCustomer() {
    this.customerFormGroup.patchValue({
      CU1_STATUS: this.customer.CU1_STATUS.toString(),
      CU1_ID: this.customer.CU1_ID,
      CORP_ADDRESS_ID: this.customer.CORP_ADDRESS_ID,
      CU1_3DCART_SECURE_URL: this.customer.CU1_3DCART_SECURE_URL,
      CU1_3DCART_TOKEN: this.customer.CU1_3DCART_TOKEN,
      CU1_830_FORWARDING_FOLDER: this.customer.CU1_830_FORWARDING_FOLDER,
      CU1_852_IMPORT_FOLDER: this.customer.CU1_852_IMPORT_FOLDER,
      CU1_ACKNOWLEDGEMENT_EMAIL: this.customer.CU1_ACKNOWLEDGEMENT_EMAIL,
      CU1_ALLOW_DUPLICATE_BARCODES: this.customer.CU1_ALLOW_DUPLICATE_BARCODES,
      CU1_ALLOW_DUPLICATE_PO_NUMBERS: this.customer.CU1_ALLOW_DUPLICATE_PO_NUMBERS,
      CU1_ALLOW_INVENTORY_MANAGEMENT: this.customer.CU1_ALLOW_INVENTORY_MANAGEMENT,
      CU1_ALWAYS_SEND_ASNS: this.customer.CU1_ALWAYS_SEND_ASNS,
      CU1_ALWAYS_SEND_INVOICES: this.customer.CU1_ALWAYS_SEND_INVOICES,
      CU1_ALWAYS_SEND_ORDER_CONFIRMATIONS: this.customer.CU1_ALWAYS_SEND_ORDER_CONFIRMATIONS,
      CU1_AMAZON_MWS_MARKET_ID_US: this.customer.CU1_AMAZON_MWS_MARKET_ID_US,
      CU1_API_PASSWORD: this.customer.CU1_API_PASSWORD,
      CU1_API_USERNAME: this.customer.CU1_API_USERNAME,
      CU1_AS2_CERTIFICATE_FILENAME: this.customer.CU1_AS2_CERTIFICATE_FILENAME,
      CU1_AS2_COMPATIBILITY_MODE: this.customer.CU1_AS2_COMPATIBILITY_MODE,
      CU1_AS2_ENCRYPT_MESSAGES: this.customer.CU1_AS2_ENCRYPT_MESSAGES,
      CU1_AS2_ENCRYPTION_ALGORITHM: this.customer.CU1_AS2_ENCRYPTION_ALGORITHM,
      CU1_AS2_KEY_LENGTH: this.customer.CU1_AS2_KEY_LENGTH,
      CU1_AS2_RECEIVE_ID: this.customer.CU1_AS2_RECEIVE_ID,
      CU1_AS2_RECEIVER_ID: this.customer.CU1_AS2_RECEIVER_ID,
      CU1_AS2_REQUEST_RECEIPT: this.customer.CU1_AS2_REQUEST_RECEIPT,
      CU1_AS2_SIGN_MESSAGES: this.customer.CU1_AS2_SIGN_MESSAGES,
      CU1_AS2_SIGNING_ALGORITHM: this.customer.CU1_AS2_SIGNING_ALGORITHM,
      CU1_AS2_TRADING_PARTNER_ID: this.customer.CU1_AS2_TRADING_PARTNER_ID,
      CU1_ASN_EMAIL: this.customer.CU1_ASN_EMAIL,
      CU1_ASN_FORMAT: this.customer.CU1_ASN_FORMAT ? this.customer.CU1_ASN_FORMAT.toString() : '0',
      CU1_ASN_FORWARDING_FOLDER: this.customer.CU1_ASN_FORWARDING_FOLDER,
      CU1_ASN_HISTORY_NUMBER_RANGE: this.customer.CU1_ASN_HISTORY_NUMBER_RANGE,
      CU1_ASN_HISTORY_TIMEFRAME: this.customer.CU1_ASN_HISTORY_TIMEFRAME,
      CU1_ASN_HISTORY_TIMEFRAME_TYPE: this.customer.CU1_ASN_HISTORY_TIMEFRAME_TYPE,
      CU1_ASN_IMPORT_FOLDER: this.customer.CU1_ASN_IMPORT_FOLDER,
      CU1_ASN_TRADING_PARTNER_ID: this.customer.CU1_ASN_TRADING_PARTNER_ID,
      CU1_AUTO_PO_LOCATIONS: this.customer.CU1_AUTO_PO_LOCATIONS,
      CU1_AWS_ACCESS_KEY: this.customer.CU1_AWS_ACCESS_KEY,
      CU1_AWS_MARKETPLACE_ID: this.customer.CU1_AWS_MARKETPLACE_ID,
      CU1_AWS_SECRET_KEY: this.customer.CU1_AWS_SECRET_KEY,
      CU1_AWS_SELLER_ID: this.customer.CU1_AWS_SELLER_ID,
      CU1_AWS_UTC_HOUR_OFFSET: this.customer.CU1_AWS_UTC_HOUR_OFFSET,
      CU1_BIG_COMMERCE_ACCESS_TOKEN: this.customer.CU1_BIG_COMMERCE_ACCESS_TOKEN,
      CU1_BIG_COMMERCE_CLIENT_ID: this.customer.CU1_BIG_COMMERCE_CLIENT_ID,
      CU1_BIG_COMMERCE_CLIENT_SECRET: this.customer.CU1_BIG_COMMERCE_CLIENT_SECRET,
      CU1_BILLTRUST_CLIENT_GUID: this.customer.CU1_BILLTRUST_CLIENT_GUID,
      CU1_BILLTRUST_DEVELOPER_GUID: this.customer.CU1_BILLTRUST_DEVELOPER_GUID,
      CU1_BOM_FORMAT: this.customer.CU1_BOM_FORMAT.toString(),
      CU1_CHANNEL_ADVISOR_ACCOUNT_ID: this.customer.CU1_CHANNEL_ADVISOR_ACCOUNT_ID,
      CU1_CHANNEL_ADVISOR_DEV_KEY: this.customer.CU1_CHANNEL_ADVISOR_DEV_KEY,
      CU1_CHANNEL_ADVISOR_LOCAL_ID: this.customer.CU1_CHANNEL_ADVISOR_LOCAL_ID,
      CU1_CHANNEL_ADVISOR_PASSWORD: this.customer.CU1_CHANNEL_ADVISOR_PASSWORD,
      CU1_CHECK_ACKNOWLEDGEMENTS: this.customer.CU1_CHECK_ACKNOWLEDGEMENTS,
      CU1_CHECK_ASN_ACKNOWLEDGEMENTS: this.customer.CU1_CHECK_ASN_ACKNOWLEDGEMENTS,
      CU1_CLASS_ID: this.customer.CU1_CLASS_ID,
      CU1_COMPANY_ID: this.customer.CU1_COMPANY_ID,
      CU1_COMPARE_ITEMS: this.customer.CU1_COMPARE_ITEMS,
      CU1_COMPLETE_SHIP_TO_NAME: this.customer.CU1_COMPLETE_SHIP_TO_NAME,
      CU1_CONSOLIDATE_ASN: this.customer.CU1_CONSOLIDATE_ASN,
      CU1_CONSOLIDATE_INVOICES: this.customer.CU1_CONSOLIDATE_INVOICES,
      CU1_CONTACT_ID: this.customer.CU1_CONTACT_ID,
      CU1_CREATED_BY: this.customer.CU1_CREATED_BY,
      CU1_CREATED_ON: this.customer.CU1_CREATED_ON,
      CU1_CTL_CLIENT_ID: this.customer.CU1_CTL_CLIENT_ID,
      CU1_CTL_USER_ID: this.customer.CU1_CTL_USER_ID,
      CU1_CUSTOMER_LIST_URL: this.customer.CU1_CUSTOMER_LIST_URL,
      CU1_CUSTOMER_SENDS_P21_SHIP_TO_ID: this.customer.CU1_CUSTOMER_SENDS_P21_SHIP_TO_ID,
      CU1_CUT_OFF_DATE: this.customer.CU1_CUT_OFF_DATE,
      CU1_CXML_PAYLOAD_ID: this.customer.CU1_CXML_PAYLOAD_ID,
      CU1_DUNS: this.customer.CU1_DUNS,
      CU1_EBAY_APP_NAME: this.customer.CU1_EBAY_APP_NAME,
      CU1_EBAY_CERT_NAME: this.customer.CU1_EBAY_CERT_NAME,
      CU1_EBAY_DEV_NAME: this.customer.CU1_EBAY_DEV_NAME,
      CU1_EBAY_TOKEN: this.customer.CU1_EBAY_TOKEN,
      CU1_EDI_VERSION: this.customer.CU1_EDI_VERSION,
      CU1_ELEMENT_SEPARATOR: this.customer.CU1_ELEMENT_SEPARATOR,
      CU1_EMAIL: this.customer.CU1_EMAIL,
      CU1_EMAIL_PASSWORD: this.customer.CU1_EMAIL_PASSWORD,
      CU1_EMAIL_PORT: this.customer.CU1_EMAIL_PORT,
      CU1_EMAIL_REQUIRES_TLS: this.customer.CU1_EMAIL_REQUIRES_TLS,
      CU1_EMAIL_SERVER: this.customer.CU1_EMAIL_SERVER,
      CU1_EMAIL_USER: this.customer.CU1_EMAIL_USER,
      CU1_ENABLE_850_FORWARDING: this.customer.CU1_ENABLE_850_FORWARDING,
      CU1_ENABLE_ORDER_ENTRY: this.customer.CU1_ENABLE_ORDER_ENTRY,
      CU1_ENABLE_ORDER_ENTRY_MASTER_DATA_SYNCHRONIZATION: this.customer.CU1_ENABLE_ORDER_ENTRY_MASTER_DATA_SYNCHRONIZATION,
      CU1_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION: this.customer.CU1_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION,
      CU1_FACILITY: this.customer.CU1_FACILITY,
      CU1_FLAG: this.customer.CU1_FLAG,
      CU1_FTP_DIRECTORY: this.customer.CU1_FTP_DIRECTORY,
      CU1_FTP_DOWNLOAD_EXCLUDE: this.customer.CU1_FTP_DOWNLOAD_EXCLUDE,
      CU1_FTP_DOWNLOAD_FILTER: this.customer.CU1_FTP_DOWNLOAD_FILTER,
      CU1_FTP_DOWNLOAD_FILTER2: this.customer.CU1_FTP_DOWNLOAD_FILTER2,
      CU1_FTP_PASSWORD: this.customer.CU1_FTP_PASSWORD,
      CU1_FTP_USER: this.customer.CU1_FTP_USER,
      CU1_IMPORT_FREIGHT_CODES: this.customer.CU1_IMPORT_FREIGHT_CODES,
      CU1_IMPORT_SCHEDULED_RELEASES_INTO_P21: this.customer.CU1_IMPORT_SCHEDULED_RELEASES_INTO_P21.toString(),
      CU1_INVALID_ITEM_SUBSTITUTE: this.customer.CU1_INVALID_ITEM_SUBSTITUTE,
      CU1_INVENTORY_FORMAT: this.customer.CU1_INVENTORY_FORMAT.toString(),
      CU1_INVENTORY_IMPORT_FOLDER: this.customer.CU1_INVENTORY_IMPORT_FOLDER,
      CU1_INVENTORY_LOCATION: this.customer.CU1_INVENTORY_LOCATION,
      CU1_INVOICE_FORMAT: this.customer.CU1_INVOICE_FORMAT.toString(),
      CU1_INVOICE_FORWARDING_FOLDER: this.customer.CU1_INVOICE_FORWARDING_FOLDER,
      CU1_INVOICE_HISTORY_NUMBER_RANGE: this.customer.CU1_INVOICE_HISTORY_NUMBER_RANGE,
      CU1_INVOICE_HISTORY_TIMEFRAME: this.customer.CU1_INVOICE_HISTORY_TIMEFRAME,
      CU1_INVOICE_HISTORY_TIMEFRAME_TYPE: this.customer.CU1_INVOICE_HISTORY_TIMEFRAME_TYPE,
      CU1_INVOICE_IMPORT_FOLDER: this.customer.CU1_INVOICE_IMPORT_FOLDER,
      CU1_INVOICE_PDF_IMPORT_FOLDER: this.customer.CU1_INVOICE_PDF_IMPORT_FOLDER,
      CU1_LOCATION_FORMAT: this.customer.CU1_LOCATION_FORMAT.toString(),
      CU1_MAGENTO_API_KEY: this.customer.CU1_MAGENTO_API_KEY,
      CU1_MAGENTO_PASSWORD: this.customer.CU1_MAGENTO_PASSWORD,
      CU1_MAGENTO_USERNAME: this.customer.CU1_MAGENTO_USERNAME,
      CU1_MAGENTO_VERSION: this.customer.CU1_MAGENTO_VERSION,
      CU1_MAP: this.customer.CU1_MAP,
      CU1_MASTER_CUSTOMER_CODE: this.customer.CU1_MASTER_CUSTOMER_CODE,
      CU1_MASTER_CUSTOMER_NAME: this.customer.CU1_MASTER_CUSTOMER_NAME,
      CU1_MODIFIED_BY: this.customer.CU1_MODIFIED_BY !== 0 ? this.customer.CU1_MODIFIED_BY : '',
      CU1_MODIFIED_ON: this.customer.CU1_MODIFIED_ON,
      CU1_NAME: this.customer.CU1_NAME,
      CU1_ONLY_SEND_CREDIT_INVOICES: this.customer.CU1_ONLY_SEND_CREDIT_INVOICES,
      CU1_ORDER_CHANGE_EMAIL: this.customer.CU1_ORDER_CHANGE_EMAIL,
      CU1_ORDER_CHECK_EMAIL: this.customer.CU1_ORDER_CHECK_EMAIL,
      CU1_ORDER_CONFIRMATION_HISTORY_NUMBER_RANGE: this.customer.CU1_ORDER_CONFIRMATION_HISTORY_NUMBER_RANGE,
      CU1_ORDER_CONFIRMATION_HISTORY_TIMEFRAME: this.customer.CU1_ORDER_CONFIRMATION_HISTORY_TIMEFRAME,
      CU1_ORDER_CONFIRMATION_HISTORY_TIMEFRAME_TYPE: this.customer.CU1_ORDER_CONFIRMATION_HISTORY_TIMEFRAME_TYPE,
      CU1_ORDER_CONFIRMATION_IMPORT_FOLDER: this.customer.CU1_ORDER_CONFIRMATION_IMPORT_FOLDER,
      CU1_ORDER_EMAIL: this.customer.CU1_ORDER_EMAIL,
      CU1_ORDER_ENTRY_TYPE: this.customer.CU1_ORDER_ENTRY_TYPE.toString(),
      CU1_ORDER_ENTRY_USE_BINS: this.customer.CU1_ORDER_ENTRY_USE_BINS,
      CU1_ORDER_FORMAT: this.customer.CU1_ORDER_FORMAT.toString(),
      CU1_ORDER_FORWARDING_FOLDER: this.customer.CU1_ORDER_FORWARDING_FOLDER,
      CU1_ORDER_PO_FORMAT: this.customer.CU1_ORDER_PO_FORMAT.toString(),
      CU1_ORDER_PRICE_OVERRIDE: this.customer.CU1_ORDER_PRICE_OVERRIDE,
      CU1_ORDER_RELEASE_EMAIL: this.customer.CU1_ORDER_RELEASE_EMAIL,
      CU1_ORDER_TYPE: this.customer.CU1_ORDER_TYPE,
      CU1_PICK_TICKET_FORMAT: this.customer.CU1_PICK_TICKET_FORMAT.toString(),
      CU1_PICK_TICKET_LOCATION_ID: this.customer.CU1_PICK_TICKET_LOCATION_ID,
      CU1_PICKUP_3DCART: this.customer.CU1_PICKUP_3DCART,
      CU1_PICKUP_AMAZON: this.customer.CU1_PICKUP_AMAZON,
      CU1_PICKUP_BIG_COMMERCE: this.customer.CU1_PICKUP_BIG_COMMERCE,
      CU1_PICKUP_BILLTRUST: this.customer.CU1_PICKUP_BILLTRUST,
      CU1_PICKUP_CHANNEL_ADVISOR: this.customer.CU1_PICKUP_CHANNEL_ADVISOR,
      CU1_PICKUP_CTL_RMA_INFO: this.customer.CU1_PICKUP_CTL_RMA_INFO,
      CU1_PICKUP_CTL_SHIPMENTS: this.customer.CU1_PICKUP_CTL_SHIPMENTS,
      CU1_PICKUP_DIRECTORY: this.customer.CU1_PICKUP_DIRECTORY,
      CU1_PICKUP_EBAY: this.customer.CU1_PICKUP_EBAY,
      CU1_PICKUP_EMAIL: this.customer.CU1_PICKUP_EMAIL,
      CU1_PICKUP_FTP: this.customer.CU1_PICKUP_FTP,
      CU1_PICKUP_HEADER_PREFIX: this.customer.CU1_PICKUP_HEADER_PREFIX,
      CU1_PICKUP_LINE_PREFIX: this.customer.CU1_PICKUP_LINE_PREFIX,
      CU1_PICKUP_MAGENTO: this.customer.CU1_PICKUP_MAGENTO,
      CU1_PICKUP_SELLER_CLOUD: this.customer.CU1_PICKUP_SELLER_CLOUD,
      CU1_PICKUP_SFTP: this.customer.CU1_PICKUP_SFTP,
      CU1_PICKUP_SHOPIFY: this.customer.CU1_PICKUP_SHOPIFY,
      CU1_PICKUP_VERACORE_SHIPMENTS: this.customer.CU1_PICKUP_VERACORE_SHIPMENTS,
      CU1_PICKUP_VERSAPAY: this.customer.CU1_PICKUP_VERSAPAY,
      CU1_PICKUP_WOOCOMMERCE: this.customer.CU1_PICKUP_WOOCOMMERCE,
      CU1_PICKUP_WORKWAVE: this.customer.CU1_PICKUP_WORKWAVE,
      CU1_PICKUP_ZENTAIL: this.customer.CU1_PICKUP_ZENTAIL,
      CU1_POST_AS2: this.customer.CU1_POST_AS2,
      CU1_POST_HTTP: this.customer.CU1_POST_HTTP,
      CU1_PRICE_CATALOG_FORMAT: this.customer.CU1_PRICE_CATALOG_FORMAT.toString(),
      CU1_PRICE_CATALOG_IMPORT_FOLDER: this.customer.CU1_PRICE_CATALOG_IMPORT_FOLDER,
      CU1_PRICE_CATALOG_ONLY_DIFFERENTIAL: this.customer.CU1_PRICE_CATALOG_ONLY_DIFFERENTIAL,
      CU1_PRICE_CATALOG_ONLY_STOCKABLE: this.customer.CU1_PRICE_CATALOG_ONLY_STOCKABLE,
      CU1_PRICE_CATALOG_PRODUCT_GROUP_ID: this.customer.CU1_PRICE_CATALOG_PRODUCT_GROUP_ID,
      CU1_PRICE_CATALOG_SUPPLIER_ID: this.customer.CU1_PRICE_CATALOG_SUPPLIER_ID,
      CU1_PRODUCT_TRANSFER_EMAIL: this.customer.CU1_PRODUCT_TRANSFER_EMAIL,
      CU1_QUOTE_FORMAT: this.customer.CU1_QUOTE_FORMAT.toString(),
      CU1_QUOTE_IMPORT_FOLDER: this.customer.CU1_QUOTE_IMPORT_FOLDER,
      CU1_RECEIVE_API_CALLS: this.customer.CU1_RECEIVE_API_CALLS,
      CU1_RECEIVE_AS2: this.customer.CU1_RECEIVE_AS2,
      CU1_RECEIVE_EDI: this.customer.CU1_RECEIVE_EDI,
      CU1_RECEIVE_FTP: this.customer.CU1_RECEIVE_FTP,
      CU1_RECEIVE_HTTP: this.customer.CU1_RECEIVE_HTTP,
      CU1_RECEIVER_GS_ID: this.customer.CU1_RECEIVER_GS_ID,
      CU1_RECEIVER_ID: this.customer.CU1_RECEIVER_ID,
      CU1_RECEIVER_QUALIFIER: this.customer.CU1_RECEIVER_QUALIFIER,
      CU1_REJECT_INVALID_ITEM_ORDERS: this.customer.CU1_REJECT_INVALID_ITEM_ORDERS,
      CU1_REJECT_ORDER_CHANGES: this.customer.CU1_REJECT_ORDER_CHANGES.toString(),
      CU1_REMITTANCE_ADVICE_FORWARDING_FOLDER: this.customer.CU1_REMITTANCE_ADVICE_FORWARDING_FOLDER,
      CU1_REMOTE_FTP_DELETE_FILES_AFTER_PICKUP: this.customer.CU1_REMOTE_FTP_DELETE_FILES_AFTER_PICKUP,
      CU1_REMOTE_FTP_DIRECTORY_PICKUP: this.customer.CU1_REMOTE_FTP_DIRECTORY_PICKUP,
      CU1_REMOTE_FTP_DIRECTORY_SEND: this.customer.CU1_REMOTE_FTP_DIRECTORY_SEND,
      CU1_REMOTE_FTP_DIRECTORY_SEND_INVENTORY: this.customer.CU1_REMOTE_FTP_DIRECTORY_SEND_INVENTORY,
      CU1_REMOTE_FTP_PASSWORD: this.customer.CU1_REMOTE_FTP_PASSWORD,
      CU1_REMOTE_FTP_SERVER: this.customer.CU1_REMOTE_FTP_SERVER,
      CU1_REMOTE_FTP_USERNAME: this.customer.CU1_REMOTE_FTP_USERNAME,
      CU1_REMOTE_HTTP_PASSWORD: this.customer.CU1_REMOTE_HTTP_PASSWORD,
      CU1_REMOTE_HTTP_SERVER: this.customer.CU1_REMOTE_HTTP_SERVER,
      CU1_REMOTE_HTTP_USERNAME: this.customer.CU1_REMOTE_HTTP_USERNAME,
      CU1_REMOTE_ORDER_DATABASE: this.customer.CU1_REMOTE_ORDER_DATABASE,
      CU1_REMOTE_ORDER_TABLE: this.customer.CU1_REMOTE_ORDER_TABLE,
      CU1_RETURNS_EMAIL: this.customer.CU1_RETURNS_EMAIL,
      CU1_ROUTING_REQUEST_FORMAT: this.customer.CU1_ROUTING_REQUEST_FORMAT.toString(),
      CU1_SAVE_867_IN_DATABASE: this.customer.CU1_SAVE_867_IN_DATABASE,
      CU1_SEGMENT_TERMINATOR: this.customer.CU1_SEGMENT_TERMINATOR,
      CU1_SELLER_CLOUD_PASSWORD: this.customer.CU1_SELLER_CLOUD_PASSWORD,
      CU1_SELLER_CLOUD_PICKUP_FBA: this.customer.CU1_SELLER_CLOUD_PICKUP_FBA,
      CU1_SELLER_CLOUD_USERNAME: this.customer.CU1_SELLER_CLOUD_USERNAME,
      CU1_SEND_ACKNOWLEDGEMENT: this.customer.CU1_SEND_ACKNOWLEDGEMENT,
      CU1_SEND_AUTO_POS: this.customer.CU1_SEND_AUTO_POS,
      CU1_SEND_CONTRACT_ORDERS: this.customer.CU1_SEND_CONTRACT_ORDERS,
      CU1_SEND_CREDIT_INVOICES: this.customer.CU1_SEND_CREDIT_INVOICES,
      CU1_SEND_CUSTOMERS_AND_ITEMS: this.customer.CU1_SEND_CUSTOMERS_AND_ITEMS,
      CU1_SEND_EDI_ASN: this.customer.CU1_SEND_EDI_ASN,
      CU1_SEND_EDI_BOM: this.customer.CU1_SEND_EDI_BOM,
      CU1_SEND_EDI_INVOICES: this.customer.CU1_SEND_EDI_INVOICES,
      CU1_SEND_EDI_LOCATIONS: this.customer.CU1_SEND_EDI_LOCATIONS,
      CU1_SEND_EDI_ORDER_CONFIRMATIONS: this.customer.CU1_SEND_EDI_ORDER_CONFIRMATIONS,
      CU1_SEND_EDI_ORDER_POS: this.customer.CU1_SEND_EDI_ORDER_POS,
      CU1_SEND_EDI_ORDERS: this.customer.CU1_SEND_EDI_ORDERS,
      CU1_SEND_EDI_PICK_TICKETS: this.customer.CU1_SEND_EDI_PICK_TICKETS,
      CU1_SEND_EDI_QUOTES: this.customer.CU1_SEND_EDI_QUOTES,
      CU1_SEND_EDI_RMAS: this.customer.CU1_SEND_EDI_RMAS,
      CU1_SEND_EDI_ROUTING_REQUESTS: this.customer.CU1_SEND_EDI_ROUTING_REQUESTS,
      CU1_SEND_EDI_TRANSFERS: this.customer.CU1_SEND_EDI_TRANSFERS,
      CU1_SEND_EMAIL: this.customer.CU1_SEND_EMAIL,
      CU1_SEND_FTP: this.customer.CU1_SEND_FTP,
      CU1_SEND_INVENTORY: this.customer.CU1_SEND_INVENTORY,
      CU1_SEND_INVENTORY_RECEIPT_ORDERS: this.customer.CU1_SEND_INVENTORY_RECEIPT_ORDERS,
      CU1_SEND_ORDER_CHECK: this.customer.CU1_SEND_ORDER_CHECK,
      CU1_SEND_PACKING_SLIPS: this.customer.CU1_SEND_PACKING_SLIPS,
      CU1_SEND_PAID_INVOICES: this.customer.CU1_SEND_PAID_INVOICES,
      CU1_SEND_PRICE_CATALOG: this.customer.CU1_SEND_PRICE_CATALOG,
      CU1_SEND_SFTP: this.customer.CU1_SEND_SFTP,
      CU1_SEND_SINGLE_LINE_INVOICES: this.customer.CU1_SEND_SINGLE_LINE_INVOICES,
      CU1_SFTP_PRIVATE_KEY_FILENAME: this.customer.CU1_SFTP_PRIVATE_KEY_FILENAME,
      CU1_SHARED_SECRET: this.customer.CU1_SHARED_SECRET,
      CU1_SHIP_CONFIRMATION_EMAIL: this.customer.CU1_SHIP_CONFIRMATION_EMAIL,
      CU1_SHIP_TO_FILTER: this.customer.CU1_SHIP_TO_FILTER,
      CU1_SHOPIFY_API_KEY: this.customer.CU1_SHOPIFY_API_KEY,
      CU1_SHOPIFY_PASSWORD: this.customer.CU1_SHOPIFY_PASSWORD,
      CU1_SHOW_DEFAULT: this.customer.CU1_SHOW_DEFAULT,
      CU1_SOLVOYO_PASSWORD: this.customer.CU1_SOLVOYO_PASSWORD,
      CU1_SOLVOYO_USERNAME: this.customer.CU1_SOLVOYO_USERNAME,
      CU1_SOURCE_IP_ADDRESS: this.customer.CU1_SOURCE_IP_ADDRESS,
      CU1_SPECIAL_ASN_NUMBERS: this.customer.CU1_SPECIAL_ASN_NUMBERS,
      CU1_SPECIAL_INVOICE_NUMBERS: this.customer.CU1_SPECIAL_INVOICE_NUMBERS,
      CU1_SPECIAL_ORDER_NUMBERS: this.customer.CU1_SPECIAL_ORDER_NUMBERS,
      CU1_STOP_IMPORT_WITH_ERRORS: this.customer.CU1_STOP_IMPORT_WITH_ERRORS,
      CU1_SUPPLIER_CODE: this.customer.CU1_SUPPLIER_CODE,
      CU1_SYNCHRONIZED: this.customer.CU1_SYNCHRONIZED,
      CU1_TAKER: this.customer.CU1_TAKER,
      CU1_TEST_MODE: this.customer.CU1_TEST_MODE,
      CU1_TRADING_PARTNER_FORWARDING_GS_ID: this.customer.CU1_TRADING_PARTNER_FORWARDING_GS_ID,
      CU1_TRADING_PARTNER_FORWARDING_ID: this.customer.CU1_TRADING_PARTNER_FORWARDING_ID,
      CU1_TRADING_PARTNER_FORWARDING_QUALIFIER: this.customer.CU1_TRADING_PARTNER_FORWARDING_QUALIFIER,
      CU1_TRADING_PARTNER_GS_ID: this.customer.CU1_TRADING_PARTNER_GS_ID,
      CU1_TRADING_PARTNER_ID: this.customer.CU1_TRADING_PARTNER_ID,
      CU1_TRADING_PARTNER_QUALIFIER: this.customer.CU1_TRADING_PARTNER_QUALIFIER,
      CU1_TRANSFER_FORMAT: this.customer.CU1_TRANSFER_FORMAT.toString(),
      CU1_TRANSFER_LOCATION_ID: this.customer.CU1_TRANSFER_LOCATION_ID,
      CU1_TXT_APPROVED: this.customer.CU1_TXT_APPROVED,
      CU1_USE_CLASS_ID: this.customer.CU1_USE_CLASS_ID,
      CU1_USE_CONTRACT: this.customer.CU1_USE_CONTRACT,
      CU1_USE_CUSTOMER_ID: this.customer.CU1_USE_CUSTOMER_ID,
      CU1_USE_P21_API: this.customer.CU1_USE_P21_API,
      CU1_USE_P21_SHIP_TO_DATA: this.customer.CU1_USE_P21_SHIP_TO_DATA,
      CU1_VERACORE_PASSWORD: this.customer.CU1_VERACORE_PASSWORD,
      CU1_VERACORE_SYSTEM_ID: this.customer.CU1_VERACORE_SYSTEM_ID,
      CU1_VERACORE_USERNAME: this.customer.CU1_VERACORE_USERNAME,
      CU1_VERSAPAY_KEY: this.customer.CU1_VERSAPAY_KEY,
      CU1_VERSAPAY_TOKEN: this.customer.CU1_VERSAPAY_TOKEN,
      CU1_WAREHOUSE_ORDER_EMAIL: this.customer.CU1_WAREHOUSE_ORDER_EMAIL,
      CU1_WOOCOMMERCE_CONSUMER_KEY: this.customer.CU1_WOOCOMMERCE_CONSUMER_KEY,
      CU1_WOOCOMMERCE_CONSUMER_SECRET: this.customer.CU1_WOOCOMMERCE_CONSUMER_SECRET,
      CU1_WOOCOMMERCE_INVENTORY_URL: this.customer.CU1_WOOCOMMERCE_INVENTORY_URL,
      CU1_WORKWAVE_KEY: this.customer.CU1_WORKWAVE_KEY,
      CU1_X12_STANDARD: this.customer.CU1_X12_STANDARD,
      CU1_ZENTAIL_TOKEN: this.customer.CU1_ZENTAIL_TOKEN,

      CU1_ORDER_IMPORT_FOLDER: this.customer.CU1_ORDER_IMPORT_FOLDER,
      CU1_ORDER_FILE_EXTENSION: this.customer.CU1_ORDER_FILE_EXTENSION,
      CU1_CLOUDSUITE_CONFIGURATION: this.customer.CU1_CLOUDSUITE_CONFIGURATION,

      CU1_SEND_CONTACTS: this.customer.CU1_SEND_CONTACTS,
      CU1_SEND_CUSTOMERS: this.customer.CU1_SEND_CUSTOMERS,
      CU1_CONTACT_FORMAT: this.customer.CU1_CONTACT_FORMAT,
      CU1_CUSTOMER_FORMAT: this.customer.CU1_CUSTOMER_FORMAT,

      CU1_PICKUP_AERO_SHIPMENTS: this.customer.CU1_PICKUP_AERO_SHIPMENTS,
      CU1_AERO_USERNAME: this.customer.CU1_AERO_USERNAME,
      CU1_AERO_PASSWORD: this.customer.CU1_AERO_PASSWORD,
      CU1_PICKUP_FREIGHTWISE_SHIPMENTS: this.customer.CU1_PICKUP_FREIGHTWISE_SHIPMENTS,
      CU1_FREIGHTWISE_USERNAME: this.customer.CU1_FREIGHTWISE_USERNAME,
      CU1_FREIGHTWISE_PASSWORD: this.customer.CU1_FREIGHTWISE_PASSWORD,
      CU1_HUBSPOT_API_KEY: this.customer.CU1_HUBSPOT_API_KEY,

      CU1_VALIDATE: this.customer.CU1_VALIDATE,
      CU1_VALIDATION_ORDER_TEMPLATE: this.customer.CU1_VALIDATION_ORDER_TEMPLATE,
      CU1_VALIDATION_ORDER_CONFIRMATION_TEMPLATE: this.customer.CU1_VALIDATION_ORDER_CONFIRMATION_TEMPLATE,
      CU1_VALIDATION_ASN_TEMPLATE: this.customer.CU1_VALIDATION_ASN_TEMPLATE,
      CU1_VALIDATION_INVOICE_TEMPLATE: this.customer.CU1_VALIDATION_INVOICE_TEMPLATE,
      CU1_VALIDATION_REPORT_EMAIL: this.customer.CU1_VALIDATION_REPORT_EMAIL,
    });
  }

  populateTicket() {
    this.customerFormGroup.patchValue({
      TICKET: {
        CU2_ID: this.ticket.CU2_ID ? this.ticket.CU2_ID : '',
        CU2_OUTBOUND_855_ORDER_CONFIRMATION: this.ticket.CU2_OUTBOUND_855_ORDER_CONFIRMATION ? parseInt(this.ticket.CU2_OUTBOUND_855_ORDER_CONFIRMATION) : false,
        CU2_OUTBOUND_856_ADVANCED_SHIP_NOTICE: this.ticket.CU2_OUTBOUND_856_ADVANCED_SHIP_NOTICE ? parseInt(this.ticket.CU2_OUTBOUND_856_ADVANCED_SHIP_NOTICE) : 0,
        CU2_OUTBOUND_810_INVOICE: this.ticket.CU2_OUTBOUND_810_INVOICE ? parseInt(this.ticket.CU2_OUTBOUND_810_INVOICE) : 0,
        CU2_OUTBOUND_846_INVENTORY_INQUIRY: this.ticket.CU2_OUTBOUND_846_INVENTORY_INQUIRY ? parseInt(this.ticket.CU2_OUTBOUND_846_INVENTORY_INQUIRY) : 0,
        CU2_OUTBOUND_832_PRICE_CATALOG: this.ticket .CU2_OUTBOUND_832_PRICE_CATALOG ? parseInt(this.ticket.CU2_OUTBOUND_832_PRICE_CATALOG) : 0,
        CU2_OUTBOUND_753_ROUTE_REQUEST: this.ticket .CU2_OUTBOUND_753_ROUTE_REQUEST ? parseInt(this.ticket.CU2_OUTBOUND_753_ROUTE_REQUEST) : 0,
        CU2_OUTBOUND_OTHER: this.ticket.CU2_OUTBOUND_OTHER || '',
        CU2_INBOUND_850_PURCHASE_ORDER: this.ticket .CU2_INBOUND_850_PURCHASE_ORDER ? parseInt(this.ticket.CU2_INBOUND_850_PURCHASE_ORDER) : 0,
        CU2_INBOUND_830_RELEASE_SCHEDULE: this.ticket .CU2_INBOUND_830_RELEASE_SCHEDULE ? parseInt(this.ticket.CU2_INBOUND_830_RELEASE_SCHEDULE) : 0,
        CU2_INBOUND_860_ORDER_CHANGE: this.ticket.CU2_INBOUND_860_ORDER_CHANGE ? parseInt(this.ticket.CU2_INBOUND_860_ORDER_CHANGE) : 0,
        CU2_INBOUND_820_PAYMENT_ADVICE: this.ticket.CU2_INBOUND_820_PAYMENT_ADVICE ? parseInt(this.ticket.CU2_INBOUND_820_PAYMENT_ADVICE) : 0,
        CU2_INBOUND_754_ROUTING: this.ticket.CU2_INBOUND_754_ROUTING ? parseInt(this.ticket.CU2_INBOUND_754_ROUTING) : 0,
        CU2_INBOUND_OTHER: this.ticket.CU2_INBOUND_OTHER || '',
        TICKET_NUMBER: this.ticket.CU2_TICKET ? this.ticket.CU2_TICKET : '',
        CU2_CREATED_BY: this.ticket.CU2_CREATED_BY ? this.ticket.CU2_CREATED_BY : '',
        CU2_CREATED_ON: this.ticket.CU2_CREATED_ON ? this.ticket.CU2_CREATED_ON : '',
        CU2_MODIFIED_BY: this.ticket.CU2_MODIFIED_BY && this.ticket.CU2_MODIFIED_BY != 0 ? this.ticket.CU2_MODIFIED_BY : '',
        CU2_MODIFIED_ON: this.ticket.CU2_MODIFIED_ON ? this.ticket.CU2_MODIFIED_ON : '',
        CU2_CONTACT_NAME: this.ticket.CU2_CONTACT_NAME ? this.ticket.CU2_CONTACT_NAME : '',
        CU2_CONTACT_PHONE: this.ticket.CU2_CONTACT_PHONE ? this.ticket.CU2_CONTACT_PHONE : '',
        CU2_CONTACT_EMAIL: this.ticket.CU2_CONTACT_EMAIL ? this.ticket.CU2_CONTACT_EMAIL : '',
      },
    });

    this.ticketUrl = this.ticket.CU2_TICKET;
  }

  populateFileList(data) {
    this.customerFileList = data.fileInfo;
    this.customerFileList.forEach((element) => {
      element.toBeRemoved = false;
    });
  }

  getNewFields() {
    this.subscriptions.push(
      this.tradingPartnerService.getNewFieldsData('cu1_customer').subscribe(
        (data) => {
          data.forEach((element) => {
            this.customerFormGroup.addControl(
              element.TP_NEW_FIELD_NAME,
              new FormControl('')
            );
          });
          this.newFields = data;
          this.thereAreNewFields = data.length > 0;
          this.populateNewFields();

          this.validateUserPermission();
          this.loading = false;
        },
        (error) => {
          this.toastr.error('Request timed out', 'Service Error');
          this.loading = false;
        }
      )
    );
  }

  populateNewFields() {
    if (this.newFields.length > 0 && this.customer) {
      this.newFields.forEach((element) => {
        const key = element['TP_NEW_FIELD_NAME'];
        const value = this.customer[element['TP_NEW_FIELD_NAME']];
        this.customerFormGroup.patchValue({ [key]: value });
      }, this);
    }
  }

  validateUserPermission() {
    if (!this.hasEditPermission) {
      this.customerFormGroup.disable();
    }
  }

  checkEmail(obj) {
    console.log('blured', obj);
    this.statusEmail = '0';
    const split = obj.split(',');
    if (/^[a-zA-Z0-9@., ]+$/.test(obj)) {
      for (let i = 0; i < split.length; i++) {
        if (/\S+@\S+\.\S{2,}/.test(split[i])){
            this.displayMessage.CU2_CONTACT_EMAIL_ERR = '';
            this.statusEmail = '1';
        } else {
          this.displayMessage.CU2_CONTACT_EMAIL_ERR = 'Valid email is required';
        }
      }
    } else {
      this.displayMessage.CU2_CONTACT_EMAIL_ERR = 'Chars Allowed: a-z A-Z 0-9';
    }
  }

  saveCustomer(action) {
    Object.keys(this.customerFormGroup.controls).forEach((key) => {
        const control = this.customerFormGroup.get(key);
        if (control) {
          control.updateValueAndValidity();
        }
    });

    if (
      this.customerFormGroup.valid &&
      (action === this.formActions.SUBMITTING ||
        this.customerFormGroup.dirty ||
        this.filesUpdated)
    ) {
      this.loading = true;
      const params = this.route.snapshot.params;
      const isNew = params['id'] === '0';
      let customer = this.customerFormGroup.getRawValue();

      let ticket = customer.TICKET;
      ticket = this.validateAndFormatTicketFields(ticket);

      customer.CU1_CREATED_BY = this.cu1CreatedById;
      if (!isNew) {
        customer = this.validateAndFormatCustomerFields(
          customer,
          this.companyId,
          action,
          isNew
        );

        this.subscriptions.push(
          this.tradingPartnerService
            .updateCustomer(
              customer,
              ticket,
              action,
              this.customerFileList,
              this.customerFiles
            )
            .subscribe(
              (result) => {
                if (result && result.success) {
                  this.toastr.success('Customer saved successfully', 'Success');

                  if (!this.hideCancelButton) {
                    setTimeout(() => {
                      this.loading = false;
                      this.onCancel();
                    }, 2000);
                  } else {
                    this.loading = false;
                  }
                } else {
                  this.toastr.error('Server error', 'Service Error');
                  this.loading = false;
                  if (result.domain) {} else {
                    this.toastr.error('Server error', 'Domain does not exist.');
                    this.loading = false;
                  }
                }
              },
              (error) => {
                this.toastr.error('Request timed out', 'Service Error');
                this.loading = false;
              }
            )
        );
      } else {
        customer = this.validateAndFormatCustomerFields(
          customer,
          this.companyId,
          action,
          isNew
        );

        this.subscriptions.push(
          this.tradingPartnerService
            .addCustomer(
              customer,
              ticket,
              action,
              this.customerFileList,
              this.customerFiles
            )
            .subscribe(
              (result) => {
                if (result && result.success) {
                  this.toastr.success('Customer added successfully', 'Success');
                  setTimeout(() => {
                    this.loading = false;
                    this.onCancel();
                  }, 2000);
                } else {
                  this.toastr.error('Server error', 'Service Error');
                  this.loading = false;
                  if (result.domain) {} else {
                    this.toastr.error('Server error', 'Domain does not exist.');
                    this.loading = false;
                  }
                }
              },
              (error) => {
                this.toastr.error('Request timed out', 'Service Error');
                this.loading = false;
              }
            )
        );
      }
    }
  }

  validateCompany(companyId) {
    this.loading = true;
    this.companyId = companyId;
    this.subscriptions.push(
      this.tradingPartnerService.preloadData().subscribe(
        (data) => {
          this.prepareData(data);
          if (companyId && companyId != 0) {
            this.customerFormGroup.controls['CO1_ID'].setValue(companyId);
            this.hasFixedCompany = true;
            this.customerFormGroup.controls['CO1_ID'].disable();
          }

          this.loading = false;
        },
        (error) => {
          this.loading = false;
        }
      )
    );
  }

  prepareData(data) {
    this.companies = data.companies.map((company) => {
      return {
        CO1_ID: company.CO1_ID,
        name: 'CO1_ID:' + company.CO1_ID,
        text: company.CO1_NAME,
      };
    });
  }

  onSave(action) {
    this.formAction = action;
    this.saveCustomer(this.formAction);

    if (this.isGuestUser) {
      this.contactEmails = this.ticket.CU2_CONTACT_EMAIL.toString().split(',');
      const dialogRef = this.dialog.open(TradingPartnerSaveModalComponent, {
        width: '800px',
        height: '400px',
        role: 'dialog',
        data: {
          contactEmails: this.contactEmails,
        },
      });

      dialogRef.afterClosed().subscribe(
          data => this.saveModifiedBy(data)
      );
    }
  }

  saveModifiedBy(logData) {
      this.loading = true;
      const customer = this.customerFormGroup.getRawValue();

      this.subscriptions.push(
        this.tradingPartnerService
        .saveToModifiedLog(customer, null, logData)
        .subscribe(
            (result) => {
              if (result && result.success) {
                this.toastr.success('Modifying user saved successfully', 'Success');
                this.loading = false;
              } else {
                this.toastr.error('Server error', 'Service Error');
                this.loading = false;
              }
            },
            (error) => {
              this.toastr.error('Request timed out', 'Service Error');
              this.loading = false;
            }
        )
    );
      this.loading = false;
      return false;
  }

  onSubmit(action) {
      this.formAction = action;
      this.saveCustomer(this.formAction);
  }

  conditionalValidator(predicate, validator) {
    return (formControl) => {
      if (!formControl.parent) {
        return null;
      }

      if (predicate()) {
        return validator(formControl);
      }

      return null;
    };
  }

  validateStatus(status, action) {
    if (action === this.formActions.SUBMITTING && status === '0') { return '1'; }
    return status;
  }

  onStatusChange(event) {
    if (event.target.checked) {
      this.status = event.target.value;
    }
  }

  validateAndFormatCustomerFields(customer, companyId, action, isNew = false) {
    if (isNew) {
      customer.CU1_CREATED_ON = new Date()
        .toISOString()
        .slice(0, 19)
        .replace('T', ' ');

      customer.CU1_CREATED_BY = this.authService.userId;

      for (const prop in customer) {
        if (!customer[prop]) {
          delete customer[prop];
        }
      }
    } else {
      customer.CO1_ID = companyId;
      customer.CU1_MODIFIED_ON = new Date()
        .toISOString()
        .slice(0, 19)
        .replace('T', ' ');

      customer.CU1_MODIFIED_BY = this.authService.userId;
      customer.CO1_ID = companyId;
      if (!customer.CU1_CUT_OFF_DATE) { delete customer.CU1_CUT_OFF_DATE; }

      customer.CU1_ASN_FORMAT = !customer.CU1_ASN_FORMAT
        ? '0'
        : customer.CU1_ASN_FORMAT;

      customer.CU1_CONSOLIDATE_ASN = !customer.CU1_CONSOLIDATE_ASN
        ? '0'
        : customer.CU1_CONSOLIDATE_ASN;
    }
    if (customer.TICKET) { delete customer.TICKET; }

    customer.CU1_STATUS = this.validateStatus(this.status, action);
    customer.CU1_SHOW_DEFAULT = customer.CU1_SHOW_DEFAULT == true ? 'X' : '';

    for (const prop in customer) {
      if (Object.prototype.hasOwnProperty.call(customer, prop)) {
        if (typeof customer[prop] === 'boolean') {
          customer[prop] = customer[prop] === true ? 1 : 0;
        }
      }
    }

    return customer;
  }

  validateAndFormatTicketFields(ticket) {
    if (!ticket.CU2_ID) {
      ticket.CU2_CREATED_ON = new Date()
        .toISOString()
        .slice(0, 19)
        .replace('T', ' ');
      ticket.CU2_CREATED_BY = this.authService.userId;
    } else {
      ticket.CU2_MODIFIED_ON = new Date()
        .toISOString()
        .slice(0, 19)
        .replace('T', ' ');
      ticket.CU2_MODIFIED_BY = this.authService.userId;
    }

    for (const prop in ticket) {
      if (Object.prototype.hasOwnProperty.call(ticket, prop)) {
        if (typeof ticket[prop] === 'boolean') {
          ticket[prop] = ticket[prop] === true ? 1 : 0;
          //console.log(prop + ':' + ticket[prop]);
        }
      }
    }

    return ticket;
  }

  mustHideCancelButton() {
    const userPermissions = this.permissionsService.getPermissions();
    return Object.keys(userPermissions).length <= 1;
  }

  uploadFiles(): void {
    const currentQuantity = this.customerFileList.length;
    const currentSize = this.customerFileList.reduce(
      (r, d) => r + parseInt(d.FI1_SIZE),
      0
    );

    const dialogRef = this.dialog.open(UploadTradingPartnerComponent, {
      width: window.innerWidth - 100 + 'px',
      role: 'dialog',
      disableClose: true,
      data: {
        maxQuantity: this.maxQuantity - currentQuantity,
        maxSize: this.maxSize - currentSize,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.filesUpdated = true;
        this.addNewFilesToList(result);
      }
    });
  }

  addNewFilesToList(files) {
    files.forEach((element) => {
      if (
        this.customerFileList.filter(function (e) {
          return e.filename === element.name && e.toBeRemoved === false;
        }).length === 0 &&
        this.customerFileList.length < this.maxNumberOfFiles
      ) {
        this.customerFileList.push({
          FI1_ORIGINAL_NAME: element.name,
          FI1_ID: null,
          FI1_CREATED_ON: null,
          FI1_SIZE: element.size,
          toBeRemoved: false,
        });

        this.customerFiles.push(element);
      }
    });
  }

  downloadFile(file) {
    if (file) {
      this.subscriptions.push(
        this.tradingPartnerService.getTradingPartnerFile(file.FI1_ID).subscribe(
          (response) => {
            const blob = new Blob([response], { type: response.type });
            saveAs(blob, file.FI1_ORIGINAL_NAME);
          },
          (error: any) => {
            this.toastr.error(
              'There was an error downloading the file',
              'Service Error'
            );
            this.loading = false;
          }
        )
      );
    }
  }

  removeFileFromList(file: any) {
    const selectedFile = file;
    this.confirmationService.confirm({
      header: 'Warning',
      message: 'Are you sure you want to delete this file?',
      acceptLabel: 'Yes, delete it',
      accept: () => {
        this.deleteFile(selectedFile);
      },
    });
  }

  deleteFile(selectedFile) {
    this.filesUpdated = true;

    // Flag file to be removed in the server
    const index = this.customerFileList.findIndex(
      (item) => item.FI1_ORIGINAL_NAME == selectedFile.FI1_ORIGINAL_NAME
    );
    if (index != -1) {
      if (
        this.customerFileList[index].FI1_CREATED_ON &&
        this.customerFileList[index].FI1_ID
      ) {
        this.customerFileList[index].toBeRemoved = true;
      } else { this.customerFileList.splice(index, 1); }
    }

    // Remove the file from file list
    this.customerFiles.splice(
      this.customerFiles.findIndex(
        (item) => item.name === selectedFile.FI1_ORIGINAL_NAME
      ),
      1
    );

    console.log('removed: ' + selectedFile.FI1_ORIGINAL_NAME);
  }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/trading-partners/tp-new-customer/tp-new-customer.component.ts